// @ts-strict-ignore
import {AttrsSchema} from 'panel';
import {Hooks} from 'snabbdom';
import {
  arrow,
  autoUpdate,
  computePosition,
  detectOverflow,
  flip,
  getOverflowAncestors,
  offset,
  Placement,
  shift,
  size,
  VirtualElement,
} from '@floating-ui/react';
import {getNearestOverflowAncestor} from '@floating-ui/utils/dom';

import {StackableComponent, Visibility, VisibilityChangeEvent} from 'common/components/base/stackable';
import {IronContextRegistry} from 'common/report/context-registry';
import {scrollIntoViewIfNeeded} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {portalify} from 'common/util/experimental-portal';
import {panel2react} from 'common/util/react';
import {isTestEnv} from 'common/report/util/env';

import {Directions, DropMenuAttrs, PositioningStrategy, PositionParentType} from './types';
import {template} from './template';
import css from './index.inline.styl';

export type PositionParent = PositionParentType | HTMLElement | VirtualElement;

const FLOATING_PADDING_TOP = 60;
const FLOATING_PADDING_BOTTOM = 60;

interface DropMenuProps {
  attrs?: DropMenuAttrs;
  on?: {
    change?: (ev: VisibilityChangeEvent) => void;
  };
  props?: {
    positionParent?: PositionParent;
  };
  hook?: Hooks;
}

const FLOATING_POSITION_OVERRIDES: {[key in Directions]: Placement} = {
  [Directions.Down]: `bottom-start`,
  [Directions.DownCenter]: `bottom`,
  [Directions.DownRight]: `bottom-end`,
  [Directions.Left]: `left-start`,
  [Directions.LeftCenter]: `left`,
  [Directions.LeftBottom]: `left-end`,
  [Directions.Right]: `right-start`,
  [Directions.RightCenter]: `right`,
  [Directions.RightBottom]: `right-end`,
  [Directions.Up]: `top-start`,
  [Directions.UpCenter]: `top`,
  [Directions.UpRight]: `top-end`,
};

const POSITION_STYLE_FIELDS = [`top`, `left`, `position`, `visibility`];

export const scrollUtils = {
  scrollIntoViewIfNeeded,
};

export class DropMenu extends StackableComponent<EmptyObj, DropMenuAttrs, IronContextRegistry> {
  // this is either the closest element to the dropdown that has overflow: auto or overflow: scroll
  // we need to expand it's padding-bottom when the dropdown is fixed positioned so that the dropdown
  // could fit into the viewport
  private declare overflowParent: HTMLElement;
  private declare overflowParentPaddingBottom: string;
  private maxPaddingBottom = 0;
  private floaterCleanup: () => void = null;
  private _positionParent: PositionParent = PositionParentType.ParentElement;
  private _positionParentEl: HTMLElement | VirtualElement = null;

  private scrolledIntoView = false;

  static get attrsSchema(): AttrsSchema<DropMenuAttrs> {
    return {
      ...super.attrsSchema,
      open: {
        description: `Is the dropdown menu open?`,
        type: `boolean`,
      },
      offset: {type: `number`, default: 4},
      skidding: {type: `number`, default: 0, description: `Amount of shifting parallel to parent`},
      'show-arrow': {type: `boolean`, description: `Whether to display an arrow b/w the tooltip and parent`},
      direction: {
        description: `Menu opens on the top of an element if 'up', on the bottom if 'down'`,
        type: `string`,
        enum: Object.values(Directions),
        default: Directions.Down,
      },
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Fixed,
        description: `Defines how the menu is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'force-stacking': {
        type: `boolean`,
        description: `Forces this dropdown to create a new stacking context regardless of positioning strategy`,
      },
      'allowed-flip-directions': {
        type: `json`,
        default: null,
        description: `Defines preferred flipping directions for whenever the dropdown cannot be fitted in view`,
      },
      'experimental-positioning': `boolean`,
      'prevent-close': {
        type: `boolean`,
        description: `When true, prevent drop menu from closing by regular means such as clicking outside of the component.`,
      },
      'prevent-stacking': {
        type: `boolean`,
        description: `When true this component will not contribute to stacking considerations, i.e.
        opening a component with this set to true will not cause another already-open DropMenu to
        close.`,
      },
      'disable-auto-focus': {
        type: `boolean`,
        description: `When true, prevent default StackableComponent behavior of focusing the first focusable child when the menu is open.`,
      },
      'disable-auto-update': {
        type: `boolean`,
        description: `When true, disable floating-ui's autoUpdate which continuously monitors and repositions the floating element. Use for non-interactive popups that don't need to track position changes.`,
      },
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
    };
  }

  set positionParent(positionParent: PositionParent) {
    positionParent ||= PositionParentType.ParentElement;

    if (this._positionParent !== positionParent) {
      this._positionParent = positionParent;
      this.updatePositionParentElement();
      this.cleanupFloatingUI();
      if (this.initialized && this.state.visibility === Visibility.Open) {
        this.setupFloatingUI();
      }
    } else if (this.floaterCleanup) {
      this.updateFloatingPosition();
    }
  }

  updatePositionParentElement() {
    if (this._positionParent === PositionParentType.ParentElement) {
      this._positionParentEl = this.parentElement;
    } else {
      this._positionParentEl = this._positionParent as HTMLElement;
    }

    return this._positionParentEl;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`open`)) {
      this.open();
    }
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  get trapTab() {
    return false;
  }

  /**
   * When a dropdown is absolute positioned relative to another container we
   * make the assumption that the container is fixed and is the top most stacking
   * context in the page, in that case we want the dropdown to display as a tooltip
   */
  get increaseStackingContext() {
    return (
      !this.attr(`prevent-stacking`) &&
      (this.attr(`force-stacking`) || this.attr(`position-strategy`) === PositioningStrategy.Fixed)
    );
  }

  /**
   * Makes sure the floating element is visible in the viewport by scrolling
   * its reference element (in most cases its parent) so that the bottom
   * edge of the floating element becomes visible
   */
  scrollFloatingElementIntoView(placement: Placement) {
    if (
      // this behavior is only desired for floating elements across the y axis
      placement.startsWith(`left`) ||
      placement.startsWith(`right`) ||
      this.attr(`position-strategy`) !== PositioningStrategy.Fixed ||
      this.scrolledIntoView ||
      this._positionParentEl instanceof Node === false
    ) {
      this.scrolledIntoView = true;
      return;
    }

    const scrollingAncestors = getOverflowAncestors(this._positionParentEl);

    // scrollingAncestors will return at least 2 elements: the window and the document
    // if one of the parent is scrollable it will be placed at position 0
    if (scrollingAncestors?.length > 2) {
      // setTimeout instead of RAF because there's a race condition on initial render
      // of drop menu where we update scrollTop on initial render but if *this*
      // call happens too fast we end up looking at the un-updated scrollTop value
      // which causes the scroll calculation to be off and we scroll the drop menu
      // out of view.
      setTimeout(
        () =>
          scrollUtils.scrollIntoViewIfNeeded(
            (this._positionParentEl as HTMLElement) || this,
            scrollingAncestors[0] as Element,
          ),
        1,
      );
    }

    this.scrolledIntoView = true;
  }

  updateFloatingPosition = () => {
    // if there's no parent element to attach to, this either means the dropdown's parent element has been
    // disconnected and out of view, setting up floating-ui is not needed in those cases
    if (
      !this._positionParentEl ||
      ((this._positionParentEl as HTMLElement)?.checkVisibility &&
        (this._positionParentEl as HTMLElement)?.checkVisibility() === false &&
        !isTestEnv())
    ) {
      this.close();
      return null;
    }

    // floating-ui does not flip placement variations (*-start or *-end) to their opposite corner if that
    // placement overflows from the alternate axis (if bottom, this is the X axis), even though the page
    // might scroll to display the overflowing area from the alternative axis, we avoid
    // that situation by defaulting to the *-end position of the desired placement
    let placement = FLOATING_POSITION_OVERRIDES[this.attr(`direction`)];
    if (this.getBoundingClientRect().right >= window.innerWidth) {
      if (placement.startsWith(`bottom`)) {
        placement = `bottom-end`;
      } else if (placement.startsWith(`top`)) {
        placement = `top-end`;
      }
    }

    const arrowEl = this.attr(`show-arrow`) ? this.el.querySelector<HTMLElement>(`.mp-drop-menu-arrow`) : null;

    computePosition(this._positionParentEl, this, {
      placement,
      strategy: this.attr(`position-strategy`),
      middleware: [
        // moves the floating element across the y axis to fit it in the viewport
        (placement.startsWith(`left`) || placement.startsWith(`right`)) && shift(),

        // hides the floating element when the reference element goes out of view
        // for example, when scrolling down a page, we don't want the floating element
        // to appear visually attached to an element out of view

        // ops: disabling this for now so we can brainstorm a better solution, it causes bad UX when
        // using a small screen. seems to have multiple false positives as well where it thinks
        // the reference element is hidden, but it is actually visible and dropdowns get closed
        // hide(),

        // pushes the floating element away from its reference
        offset(this.attr(`offset`) + (this.attr(`show-arrow`) ? 4 : 0)),

        // adds an arrow pointer around the floating element
        arrowEl && arrow({padding: 4, element: arrowEl}),

        // prevents the floating element to overflow the screen
        flip({
          rootBoundary: `viewport`,
          fallbackStrategy: `bestFit`,
          padding: {top: FLOATING_PADDING_TOP, bottom: FLOATING_PADDING_BOTTOM},
          ...(this.attr(`allowed-flip-directions`)
            ? {
                fallbackPlacements: this.attr(`allowed-flip-directions`)?.map(
                  (direction) => FLOATING_POSITION_OVERRIDES[direction],
                ),
              }
            : {}),
        }),
        // resizes the floating element so that it fits into screen
        this.attr(`experimental-positioning`) &&
          size({
            apply({availableHeight, elements}) {
              Object.assign(elements.floating.style, {
                maxHeight: `${availableHeight}px`,
              });
            },
          }),
        // this middleware expands the overflow parent's padding bottom to make sure the dropdown is not clipped if it's fixed positioned
        !this.attr(`experimental-positioning`) && {
          name: `expandOverflowParent`,
          fn: async (state) => {
            const paddingBottom = Number.parseInt(this.overflowParentPaddingBottom, 10);
            const overflow = await detectOverflow(state, {
              padding: {
                bottom: paddingBottom + FLOATING_PADDING_BOTTOM,
              },
            });
            if (placement.startsWith(`bottom`) && overflow.bottom > 0) {
              return {
                data: {
                  newPaddingBottom: overflow.bottom,
                },
              };
            }
            return {};
          },
        },
      ],
    }).then(({x, y, placement: finalPlacement, strategy, middlewareData}) => {
      const {referenceHidden, escaped} = middlewareData.hide ?? {referenceHidden: false, escaped: false};
      Object.assign(this.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: strategy,
        visibility: referenceHidden || escaped ? `hidden` : `visible`,
      });

      this.setAttribute(`data-placement`, finalPlacement);

      if (middlewareData.arrow) {
        const {x: arrowX, y: arrowY} = middlewareData.arrow;
        Object.assign(arrowEl.style, {
          left: `${arrowX}px`,
          top: `${arrowY}px`,
        });
      }

      // whenever a floating element goes out of view trigger a close event so that when scroll
      // location changes later, the floating element does not suddenly appears in view
      // disables this behavior for test environments since tests scroll up and down
      // in high succession and causes this behavior to unintentionally close dropdowns
      if (isTestEnv() === false && (referenceHidden || escaped)) {
        this.close();
      }
      if (middlewareData.expandOverflowParent?.newPaddingBottom && this.overflowParent?.style) {
        // we need to do this maxPadding because when we resize the overflowParent, the autoUpdate will re-calculate overflows creates a "damping" effect
        // usually the initial calculated newPaddingBottom is the maximum and correct value, we want to stick with that
        this.maxPaddingBottom = Math.max(this.maxPaddingBottom, middlewareData.expandOverflowParent.newPaddingBottom);
        this.overflowParent.style.paddingBottom = `${this.maxPaddingBottom}px`;
      }
      this.scrollFloatingElementIntoView(finalPlacement);
    });
  };

  /**
   * Setups the positioning library for the dropdown, for more info see:
   * https://floating-ui.com/docs/getting-started
   */
  setupFloatingUI() {
    // if there's no parent element to attach to, this either means the dropdown's parent element has been
    // disconnected and out of view, setting up the floating element is not needed in those cases
    if (!this._positionParentEl) {
      this.close();
      return null;
    }

    if (this.attr(`position-strategy`) === PositioningStrategy.Fixed) {
      this.style.position = `fixed`;
    }

    this.overflowParent = getNearestOverflowAncestor(this._positionParentEl as HTMLElement);
    this.overflowParentPaddingBottom = this.overflowParent
      ? getComputedStyle(this.overflowParent, null).paddingBottom
      : `0px`;

    // Skip autoUpdate for non-interactive popups that don't need to track position changes.
    // This prevents layout thrashing caused by continuous position monitoring.
    if (!this.attr(`disable-auto-update`)) {
      this.floaterCleanup = autoUpdate(this._positionParentEl, this, this.updateFloatingPosition, {
        animationFrame: false,
      });
    } else {
      // When autoUpdate is disabled, we still need to ensure proper initial positioning.
      // Use RAF to ensure the DOM is ready before calculating position.
      requestAnimationFrame(() => {
        this.updateFloatingPosition();
      });
    }

    return this.floaterCleanup;
  }

  protected onOpen() {
    super.onOpen();
    this.updatePositionParentElement();
    this.setupFloatingUI();
    this.updateFloatingPosition();
  }

  clearPositionStyle() {
    for (const field of POSITION_STYLE_FIELDS) {
      this.style[field] = null;
    }
  }

  /** gets called on disconnectedCallback from the StackableComponent implementation */
  protected onClose() {
    super.onClose();
    this.cleanupFloatingUI();
    if (this.overflowParent?.style) {
      this.overflowParent.style.paddingBottom = this.overflowParentPaddingBottom;
    }
    this.maxPaddingBottom = 0;
    this._positionParentEl = null;
    this.removeAttribute(`data-placement`);
    this.clearPositionStyle();
  }

  cleanupFloatingUI() {
    if (this.floaterCleanup) {
      this.floaterCleanup();
      this.floaterCleanup = null;
      this.scrolledIntoView = false;
    }
  }

  isCloseable() {
    return !this.attr(`prevent-close`);
  }

  get shouldAutoFocus(): boolean {
    return !this.attr(`disable-auto-focus`);
  }
}

export const DropMenuEl = defineAndMakeJsxFactory<DropMenuProps>(`mp-drop-menu`, DropMenu);
export const DropMenuReact = panel2react<DropMenuProps>(`mp-drop-menu`, DropMenu);
export const ExperimentalDropMenuEl = portalify(DropMenuEl);

export {Directions, PositioningStrategy, PositionParentType} from './types';
